import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ExternalLinkIcon, UsersIcon, XIcon, ClipboardListIcon, SearchIcon, LoaderIcon, AlertCircleIcon, TagIcon, FolderIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { NonprofitScroller } from './NonprofitScroller';
import { CATEGORIES } from '../data/nonprofit-categories';
const NONPROFIT_DIRECTORY_SHEET_URL = 'https://script.google.com/macros/s/AKfycbxrqFpuyVIC_Wg8j3cULjE1mlS682m_Dt5kdAlL_wkOd2TfpoZSWjIoANVgGbbNVJAH/exec';
const NONPROFIT_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbyUUpnMnKlAgNxNBXGn424XnGDhoFD1703PL7C8zujLV10PWc07wpAlCjpI6d8bXGCLaw/exec';
const ACCORDION_COLORS = [{
  gradient: 'from-haa-blue to-haa-navy',
  borderColor: 'border-haa-blue',
  accentColor: 'text-haa-blue',
  bgColor: 'bg-blue-50'
}, {
  gradient: 'from-haa-midblue to-haa-blue',
  borderColor: 'border-haa-midblue',
  accentColor: 'text-haa-midblue',
  bgColor: 'bg-blue-50/50'
}, {
  gradient: 'from-haa-skyblue to-haa-midblue',
  borderColor: 'border-haa-skyblue',
  accentColor: 'text-haa-skyblue',
  bgColor: 'bg-sky-50'
}, {
  gradient: 'from-haa-navy to-blue-900',
  borderColor: 'border-haa-navy',
  accentColor: 'text-haa-navy',
  bgColor: 'bg-gray-50'
}];
// Build a map from English section labels to Spanish translations
const SECTION_TRANSLATIONS: Record<string, string> = {
  'Community Transformation': 'Transformación Comunitaria'
};
CATEGORIES.forEach(cat => {
  if (cat.labelEs) {
    SECTION_TRANSLATIONS[cat.label] = cat.labelEs;
  }
});
interface DirectoryNonprofit {
  ein: string;
  name: string;
  website: string;
  section: string;
  focusAreas: string[];
  description: string;
  descriptionEs: string;
}
export function NonprofitDirectory() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  // Directory State
  const [nonprofits, setNonprofits] = useState<DirectoryNonprofit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  // Form State
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactName, setContactName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [is501c3, setIs501c3] = useState<'yes' | 'no' | 'not-sure' | null>(null);
  const [ein, setEin] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [socialMedia, setSocialMedia] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const resetForm = () => {
    setContactName('');
    setOrgName('');
    setIs501c3(null);
    setEin('');
    setPhone('');
    setEmail('');
    setWebsite('');
    setSocialMedia('');
    setCategory('');
    setLocation('');
    setDescription('');
    setSubmitted(false);
  };
  const handleSubmit = () => {
    const data = {
      contactName,
      orgName,
      is501c3,
      ein: is501c3 === 'yes' ? ein : '',
      phone,
      email,
      website,
      socialMedia,
      category,
      location,
      description,
      timestamp: new Date().toISOString()
    };
    fetch(NONPROFIT_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(data)
    }).catch(err => {
      console.warn('Nonprofit Google Sheets submission error:', err);
    });
    setSubmitted(true);
  };
  const isValid = contactName.trim() && orgName.trim() && email.trim() && description.trim();
  // Directory Fetch Logic
  const fetchNonprofits = useCallback(async (force = false) => {
    if (hasFetched && !force) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(NONPROFIT_DIRECTORY_SHEET_URL, {
        redirect: 'follow'
      });
      const text = await response.text();
      const cleanText = text.trim().replace(/^\uFEFF/, '');
      let json: any;
      try {
        json = JSON.parse(cleanText);
      } catch (e) {
        throw new Error('Invalid JSON response');
      }
      const rows = json.data || json.businesses || (Array.isArray(json) ? json : []);
      const mapped: DirectoryNonprofit[] = [];
      rows.forEach((row: any) => {
        const name = String(row['Organization Name'] || row.name || row.Name || '').trim();
        if (!name) return;
        const focusAreasStr = String(row['Focus Areas'] || row.focusAreas || row.FocusAreas || '').trim();
        const focusAreas = focusAreasStr ? focusAreasStr.split(',').map(s => s.trim()).filter(Boolean) : [];
        mapped.push({
          ein: String(row['EIN'] || row.ein || '').trim(),
          name,
          website: String(row['Website'] || row.website || row.url || '').trim(),
          section: String(row['Dropdown Section'] || row.section || row.Section || 'Other').trim(),
          focusAreas,
          description: String(row['Description English'] || row['Description'] || row.description || '').trim(),
          descriptionEs: String(row['Description Spanish'] || '').trim()
        });
      });
      setNonprofits(mapped);
      setHasFetched(true);
    } catch (err) {
      console.error('Error fetching nonprofits:', err);
      setError(isEs ? 'No se pudieron cargar las organizaciones.' : 'Could not load organizations.');
    } finally {
      setLoading(false);
    }
  }, [hasFetched, isEs]);
  useEffect(() => {
    if (titleInView && !hasFetched) {
      fetchNonprofits();
    }
  }, [titleInView, hasFetched, fetchNonprofits]);
  const filteredNonprofits = nonprofits.filter(org => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return org.name.toLowerCase().includes(q) || org.description.toLowerCase().includes(q) || org.descriptionEs.toLowerCase().includes(q) || org.section.toLowerCase().includes(q) || org.focusAreas.some(fa => fa.toLowerCase().includes(q));
  });
  const groupedNonprofits = filteredNonprofits.reduce((acc, org) => {
    if (!acc[org.section]) acc[org.section] = [];
    acc[org.section].push(org);
    return acc;
  }, {} as Record<string, DirectoryNonprofit[]>);
  const sortedSections = Object.keys(groupedNonprofits).sort((a, b) => a.localeCompare(b));
  // Helper to get translated section name
  const translateSection = (section: string): string => {
    if (!isEs) return section;
    return SECTION_TRANSLATIONS[section] || section;
  };
  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <>
      <section id="nonprofit-directory" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="nonprofit-directory-heading" data-id="element-3071" data-mp="iebscn1tkfhm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3072" data-mp="n4s482pzrt1v">
          {/* Header */}
          <div ref={titleRef} className="text-center mb-14" data-id="element-3073" data-mp="s4muc41y0elv">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={titleInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} data-id="element-3074" data-mp="s67o3rgfntio">
              <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3075" data-mp="1mfhypszm5nsf">
                {isEs ? 'Recursos y Apoyo' : 'Resources & Support'}
              </span>
              <h2 id="nonprofit-directory-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3076" data-mp="1wdnx40m7obsj">
                {isEs ? 'Directorio de Organizaciones' : 'Nonprofit Directory'}
              </h2>
              <div className="flex items-center justify-center gap-3 mt-4" data-id="element-3077" data-mp="13pjqncvtfuos">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3078" data-mp="yr8jxb0ti0as" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3079" data-mp="1detlv1lyhth4" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3080" data-mp="1dgwiw5lj0mvc" />
              </div>
              <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-3081" data-mp="18ma4ruo28g7k">
                {'A directory of US Based Non Profit organizations serving Honduras or the Honduran-American community.'}
              </p>
              <div className="mt-5 max-w-2xl mx-auto bg-haa-skyblue/10 border border-haa-skyblue/30 rounded-xl px-5 py-4 text-sm font-nunito text-gray-700 leading-relaxed" data-id="element-3082" data-mp="2rnb2twkc4vhz">
                {isEs ? 'Trabajamos continuamente para verificar organizaciones sin fines de lucro registradas como 501(c)(3) que sirven a Honduras y la diáspora hondureño-americana. La inclusión en este directorio no constituye un respaldo. Por favor, haga su propia investigación antes de donar o ser voluntario.\n\nSi tiene preguntas, comuníquese con nosotros \u2014 podemos ayudarle a encontrar y conectarse con organizaciones de confianza.' : 'We continuously work to vet registered 501(c)(3) nonprofits serving Honduras and the Honduran-American diaspora. Inclusion in this directory does not constitute an endorsement. Please do your due diligence before donating or volunteering. \n\nIf you have questions, reach out to us \u2014 we can help you find and connect with reputable organizations.'}
              </div>
            </motion.div>
          </div>

          {/* Featured Nonprofit Scroller */}
          <div className="mb-14" data-id="element-3083" data-mp="13xemfc7wh1k6">
            <NonprofitScroller data-id="element-3084" data-mp="18uomrmwtuxms" />
          </div>

          {/* Directory Content */}
          <div className="max-w-3xl mx-auto" data-id="element-3085" data-mp="v30759nddr12">
            {/* Search Bar */}
            <div className="mb-8" data-id="element-3086" data-mp="v0xjuxdlqc01">
              <div className="relative" data-id="element-3087" data-mp="z3k5uhnld2pb">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3088" data-mp="z2iv6eahmi84">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3089" data-mp="l39g5hy0pu5q" />
                </div>
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={isEs ? 'Buscar por nombre, descripción o área de enfoque...' : 'Search by name, description, or focus area...'} className="block w-full pl-11 pr-10 py-3.5 border border-gray-200 rounded-2xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-haa-blue focus:border-haa-blue sm:text-sm font-nunito transition-colors shadow-sm" data-id="element-3090" data-mp="q58h6l6acezi" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Clear search" data-id="element-3091" data-mp="3v2cn0esc13fr">
                    <XIcon className="h-5 w-5" data-id="element-3092" data-mp="8njh2q2nz91h" />
                  </button>}
              </div>
              {searchQuery && <div className="text-center mt-3" data-id="element-3093" data-mp="3txcll6nmbgr">
                  <span className="font-nunito text-xs font-semibold text-haa-blue bg-blue-50 px-3 py-1 rounded-full" data-id="element-3094" data-mp="ijmrej7o6dzi">
                    {isEs ? `Mostrando ${filteredNonprofits.length} resultados` : `Showing ${filteredNonprofits.length} results`}
                  </span>
                </div>}
            </div>

            {/* States */}
            {loading && <div className="flex flex-col items-center justify-center py-12 gap-3" data-id="element-3095" data-mp="mmj27nqyx5sw">
                <LoaderIcon className="w-8 h-8 text-haa-blue animate-spin" data-id="element-3096" data-mp="2tcple7g1r8q6" />
                <p className="font-nunito text-sm text-gray-500 font-semibold" data-id="element-3097" data-mp="2oe610bh3q4zx">
                  {isEs ? 'Cargando directorio...' : 'Loading directory...'}
                </p>
              </div>}

            {error && !loading && <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white rounded-2xl border border-red-100 shadow-sm" data-id="element-3098" data-mp="x9kwayget4jb">
                <AlertCircleIcon className="w-8 h-8 text-red-500" data-id="element-3099" data-mp="169r5i852rico" />
                <p className="font-nunito text-sm text-red-600 text-center" data-id="element-3100" data-mp="16b29a7yisq3s">
                  {error}
                </p>
                <button onClick={() => {
              setHasFetched(false);
              fetchNonprofits(true);
            }} className="font-nunito text-sm text-haa-navy underline hover:opacity-70 font-semibold" data-id="element-3101" data-mp="1b6zogay1rqez">
                  {isEs ? 'Reintentar' : 'Try again'}
                </button>
              </div>}

            {hasFetched && filteredNonprofits.length === 0 && !error && <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-gray-100 shadow-sm" data-id="element-3102" data-mp="1u1506kqp7q0t">
                <FolderIcon className="w-12 h-12 text-gray-300 mb-3" data-id="element-3103" data-mp="3jgyuv5hibeqr" />
                <p className="font-nunito text-gray-500" data-id="element-3104" data-mp="3je2xbi9kms2u">
                  {isEs ? 'No se encontraron organizaciones.' : 'No organizations found.'}
                </p>
              </div>}

            {/* Accordions */}
            <div className="flex flex-col gap-4" data-id="element-3105" data-mp="35gxtloxby5ar">
              {sortedSections.map((section, index) => {
              const sectionOrgs = groupedNonprofits[section];
              const colorTheme = ACCORDION_COLORS[index % ACCORDION_COLORS.length];
              const isOpen = openSections[section] || false;
              return <motion.div key={section} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: index * 0.05
              }} className={`rounded-2xl border-l-4 ${colorTheme.borderColor} bg-white shadow-md overflow-hidden`} data-id="element-3106" data-mp="3tdtqngmeb11c" data-mpi={index}>
                    <button onClick={() => toggleSection(section)} aria-expanded={isOpen} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-3107" data-mp="4j1806jwbetz">
                      <div className="flex items-center gap-3" data-id="element-3108" data-mp="hfmjwtaps86">
                        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${colorTheme.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-3109" data-mp="jth84k2d2f7">
                          <FolderIcon className="w-4.5 h-4.5 text-white w-[18px] h-[18px]" data-id="element-3110" data-mp="s7tuzdijiccg" />
                        </div>
                        <span className={`font-bebas text-xl tracking-wider ${colorTheme.accentColor}`} data-id="element-3111" data-mp="egytimadexiw">
                          {translateSection(section)}
                        </span>
                        <span className="font-nunito text-xs text-gray-400 font-semibold" data-id="element-3112" data-mp="175dyefzsfzgi">
                          {sectionOrgs.length}{' '}
                          {isEs ? 'organizaciones' : 'organizations'}
                        </span>
                      </div>
                      <motion.div animate={{
                    rotate: isOpen ? 180 : 0
                  }} transition={{
                    duration: 0.25,
                    ease: [0.16, 1, 0.3, 1]
                  }} className="flex-shrink-0 ml-2" data-id="element-3113" data-mp="1ham566vx6q49">
                        <ChevronDownIcon className={`w-5 h-5 ${colorTheme.accentColor}`} data-id="element-3114" data-mp="1q4hbhi8s2mlh" />
                      </motion.div>
                    </button>

                    <AnimatePresence initial={false} data-id="element-3115" data-mp="yc9w0l50dg8o">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.35,
                    ease: [0.16, 1, 0.3, 1]
                  }} className="overflow-hidden" data-id="element-3116" data-mp="1caq3ieap3arv">
                          <div className="border-t border-gray-100 px-5 py-4 flex flex-col gap-4" data-id="element-3117" data-mp="2qrhxt6wcpnga">
                            {sectionOrgs.map((org, i) => <motion.div key={`${org.ein}-${i}`} initial={{
                        opacity: 0,
                        x: -8
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        duration: 0.3,
                        delay: i * 0.05
                      }} className={`rounded-xl ${colorTheme.bgColor} border border-gray-100 p-4 hover:shadow-sm transition-shadow`} data-id="element-3118" data-mp="2zsg09kr55mwr" data-mpi={i}>
                                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3" data-id="element-3119" data-mp="2lsohz7tldkcy">
                                  <div className="flex-1 min-w-0" data-id="element-3120" data-mp="1odsqp4idlmgu">
                                    <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-3121" data-mp="1ob6h6zbxcwvz">
                                      <h3 className={`font-nunito font-bold text-base ${colorTheme.accentColor} leading-snug`} data-id="element-3122" data-mp="1taaqdpyumumb">
                                        {org.name}
                                      </h3>
                                      {org.ein && <span className="font-mono text-[10px] text-gray-400 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-3123" data-mp="1buf1zokp6z35">
                                          EIN: {org.ein}
                                        </span>}
                                    </div>

                                    {(isEs ? org.descriptionEs || org.description : org.description) && <p className="font-nunito text-sm text-gray-600 leading-relaxed mt-1.5 whitespace-pre-line" data-id="element-3124" data-mp="y175pjlo9ff4">
                                        {isEs ? org.descriptionEs || org.description : org.description}
                                      </p>}

                                    {org.focusAreas.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3" data-id="element-3125" data-mp="j7u7wgcu88vw">
                                        {org.focusAreas.map((fa, idx) => <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-gray-200 font-nunito text-[10px] font-semibold text-gray-500" data-id="element-3126" data-mp="231415v63xk91" data-mpi={idx}>
                                            <TagIcon className="w-2.5 h-2.5" data-id="element-3127" data-mp="1t36dp9w2nrhh" />
                                            {fa}
                                          </span>)}
                                      </div>}
                                  </div>

                                  {org.website && <a href={org.website.startsWith('http') ? org.website : `https://${org.website}`} target="_blank" rel="noopener noreferrer" aria-label={`${isEs ? 'Visitar sitio web de' : 'Visit website for'} ${org.name}`} className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-br ${colorTheme.gradient} text-white font-nunito text-xs font-bold shadow-sm hover:scale-105 transition-transform mt-2 sm:mt-0`} data-id="element-3128" data-mp="1k8rrsoh1dipe">
                                      {isEs ? 'Sitio Web' : 'Website'}
                                      <ExternalLinkIcon className="w-3 h-3" data-id="element-3129" data-mp="mo9s3s3jp9pb" />
                                    </a>}
                                </div>
                              </motion.div>)}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>;
            })}
            </div>
          </div>

          {/* Footer note */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.7
        }} className="mt-12 max-w-3xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-haa-blue/5 border border-haa-blue/20 rounded-2xl p-6" data-id="element-3130" data-mp="15mvs13nbsurn">
            <div className="flex items-start gap-4" data-id="element-3131" data-mp="1oe5cu92jr5yj">
              <UsersIcon className="w-6 h-6 text-haa-blue flex-shrink-0 mt-0.5" data-id="element-3132" data-mp="1oeom71e4sm0y" />
              <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-3133" data-mp="1jgn16ax5acmu">
                <span className="font-bold text-haa-navy block mb-1" data-id="element-3134" data-mp="1vx92wdirxcc1">
                  {isEs ? '¿Conoce una organización que deberíamos agregar?' : 'Know an organization we should add?'}
                </span>
                {isEs ? 'Si usted representa o conoce una organización sin fines de lucro que sirve a la comunidad hondureño-americana, nos encantaría incluirla en este directorio.' : "If you represent or know of a nonprofit serving the Honduran-American community, we'd love to include them in this directory."}
              </p>
            </div>
            <button onClick={() => setModalOpen(true)} className="flex-shrink-0 w-full sm:w-auto py-2.5 px-5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors whitespace-nowrap" data-id="element-3135" data-mp="186mvmoglfw33">
              {isEs ? 'Sugerir Organización' : 'Submit Organization'}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Form Modal */}
      <AnimatePresence data-id="element-3136" data-mp="pg5g5lkruwzo">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => {
        setModalOpen(false);
        if (submitted) resetForm();
      }} data-id="element-3137" data-mp="3151uxiq80tl8">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-3138" data-mp="3jvtaj20sxg82">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-3139" data-mp="23i2y97zj3lnc">
                <div className="flex items-center gap-3" data-id="element-3140" data-mp="9z3xkapfld40">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-3141" data-mp="a0ou25vb364i">
                    <ClipboardListIcon className="w-5 h-5 text-white" data-id="element-3142" data-mp="89eff7hetms" />
                  </div>
                  <h2 className="font-bebas text-2xl sm:text-3xl text-haa-navy tracking-wider leading-none pt-1" data-id="element-3143" data-mp="3rg3xp7hpkfij">
                    {isEs ? 'Organizaciones que Apoyan a Honduras y Hondureño-Americanos' : 'Nonprofits Supporting Honduras & Honduran-Americans'}
                  </h2>
                </div>
                <button onClick={() => {
              setModalOpen(false);
              if (submitted) resetForm();
            }} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0" aria-label="Close" data-id="element-3144" data-mp="3dgm5ei57ek4f">
                  <XIcon className="w-5 h-5" data-id="element-3145" data-mp="16p3fuwvdvpzs" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-3146" data-mp="1f6n8yixq3zk6">
                {submitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center justify-center py-10 text-center" data-id="element-3147" data-mp="1j7paj6f44npb">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4" data-id="element-3148" data-mp="1sx0kswdqjyes">
                      <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-3149" data-mp="w9cdzaj4jsaj">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-3150" data-mp="1587mwudjzghj" />
                      </svg>
                    </div>
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-2" data-id="element-3151" data-mp="1a8lsm5romece">
                      {isEs ? '¡Gracias por su Sugerencia!' : 'Thank You for Your Submission!'}
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm max-w-md" data-id="element-3152" data-mp="2loah51kl3lfb">
                      {isEs ? 'Hemos recibido la información de la organización. Nuestro equipo la revisará pronto para considerarla en nuestro directorio.' : 'We have received the organization information. Our team will review it soon for consideration in our directory.'}
                    </p>
                  </motion.div> : <div className="space-y-5" data-id="element-3153" data-mp="2e0nvdjl47ar">
                    <p className="font-nunito text-gray-500 text-sm" data-id="element-3154" data-mp="hc2u5vi036vp">
                      {isEs ? 'Complete el formulario a continuación para sugerir una organización sin fines de lucro para nuestro directorio.' : 'Fill out the form below to suggest a nonprofit organization for our directory.'}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3155" data-mp="m4dv91oh8ypu">
                      <div data-id="element-3156" data-mp="102kjdq3mjkcb">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3157" data-mp="103vmrot5qq2w">
                          {isEs ? 'Nombre de Contacto' : 'Contact Name'}{' '}
                          <span className="text-red-500" data-id="element-3158" data-mp="1epj9nhqqw8pg">*</span>
                        </label>
                        <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3159" data-mp="1jp5o1uib7uzu" />
                      </div>
                      <div data-id="element-3160" data-mp="vy9xt1ti57up">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3161" data-mp="vy08lgga74q2">
                          {isEs ? 'Nombre de la Organización' : 'Organization Name'}{' '}
                          <span className="text-red-500" data-id="element-3162" data-mp="m2yjgkm0a2nh">*</span>
                        </label>
                        <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3163" data-mp="r0qc5guh5ssq" />
                      </div>
                    </div>

                    <div data-id="element-3164" data-mp="2jqiqgtmg73y8">
                      <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3165" data-mp="2exhaeiai8yd2">
                        {isEs ? '¿Es una organización 501(c)(3) registrada?' : 'Is this a registered 501(c)(3)?'}
                      </label>
                      <div className="flex flex-wrap gap-3" data-id="element-3166" data-mp="251mmp8rfcd3f">
                        <button type="button" onClick={() => setIs501c3('yes')} className={`px-5 py-3 rounded-xl border font-nunito text-sm transition-all ${is501c3 === 'yes' ? 'bg-haa-blue text-white border-haa-blue' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-3167" data-mp="32ntt8o7agfif">
                          {isEs ? 'Sí' : 'Yes'}
                        </button>
                        <button type="button" onClick={() => setIs501c3('no')} className={`px-5 py-3 rounded-xl border font-nunito text-sm transition-all ${is501c3 === 'no' ? 'bg-haa-blue text-white border-haa-blue' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-3168" data-mp="1ha8v5513klji">
                          {isEs ? 'No' : 'No'}
                        </button>
                        <button type="button" onClick={() => setIs501c3('not-sure')} className={`px-5 py-3 rounded-xl border font-nunito text-sm transition-all ${is501c3 === 'not-sure' ? 'bg-haa-blue text-white border-haa-blue' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-3169" data-mp="3sy3xoudsnplr">
                          {isEs ? 'No estoy seguro' : 'Not Sure'}
                        </button>
                      </div>
                    </div>

                    <AnimatePresence data-id="element-3170" data-mp="khr8zqjm743v">
                      {is501c3 === 'yes' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-3171" data-mp="kial2qml2y9z">
                          <div className="pt-1" data-id="element-3172" data-mp="x0d76a2jyjy5">
                            <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3173" data-mp="wz22m81a985z">
                              {isEs ? 'Número EIN' : 'EIN Number'}
                            </label>
                            <input type="text" value={ein} onChange={e => setEin(e.target.value)} placeholder="XX-XXXXXXX" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3174" data-mp="16poeps9sjgjx" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3175" data-mp="o19lcyc03qfy">
                      <div data-id="element-3176" data-mp="izk7zc1b30op">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3177" data-mp="izairqo34xk2">
                          {isEs ? 'Teléfono' : 'Phone'}
                        </label>
                        <input type="tel" value={phone} onChange={e => setPhone(formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3178" data-mp="1uf900htf2pwd" />
                      </div>
                      <div data-id="element-3179" data-mp="38bilo81792m7">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3180" data-mp="38ctpu8yk4cdu">
                          {isEs ? 'Correo Electrónico' : 'Email'}{' '}
                          <span className="text-red-500" data-id="element-3181" data-mp="3y3nmc6mkovw">*</span>
                        </label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={isEs ? 'correo@ejemplo.com' : 'email@example.com'} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3182" data-mp="3mbth7odgf5dp" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3183" data-mp="1plllyl46j7sk">
                      <div data-id="element-3184" data-mp="1pjhghyqmu0q6">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3185" data-mp="1ukeyobbn6uro">
                          {isEs ? 'Sitio Web' : 'Website'}
                        </label>
                        <input type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3186" data-mp="23cqobuyxme6l" />
                      </div>
                      <div data-id="element-3187" data-mp="1fn4m0yn9jiud">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3188" data-mp="1fluqag3vumqw">
                          {isEs ? 'Redes Sociales' : 'Social Media Handle'}
                        </label>
                        <input type="text" value={socialMedia} onChange={e => setSocialMedia(e.target.value)} placeholder="@handle" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3189" data-mp="2r4nnalha9bqa" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3190" data-mp="2weq14tllhngg">
                      <div data-id="element-3191" data-mp="2sefzft4u5j7e">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3192" data-mp="2sd4qlewj9l9f">
                          {isEs ? 'Categoría' : 'Category'}
                        </label>
                        <input type="text" value={category} onChange={e => setCategory(e.target.value)} placeholder={isEs ? 'Ej. Educación, Salud' : 'e.g. Education, Health'} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3193" data-mp="2ieyqn2uaep77" />
                      </div>
                      <div data-id="element-3194" data-mp="7nfgtgaospnl">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3195" data-mp="7pa2iwts8lsq">
                          {isEs ? 'Ubicación / Área de Servicio' : 'Location / Service Area'}
                        </label>
                        <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder={isEs ? 'Ej. Houston, TX o Nacional' : 'e.g. Houston, TX or Nationwide'} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3196" data-mp="25zhaajhqm3m2" />
                      </div>
                    </div>

                    <div data-id="element-3197" data-mp="2awhfytaod4cn">
                      <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3198" data-mp="2ax0ppmq68mfl">
                        {isEs ? 'Descripción de la Organización' : 'Organization Description'}{' '}
                        <span className="text-red-500" data-id="element-3199" data-mp="zh2i7nan68gh">*</span>
                      </label>
                      <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder={isEs ? 'Describa brevemente la misión y los servicios de la organización...' : "Briefly describe the organization's mission and services..."} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm resize-none" data-id="element-3200" data-mp="14gye0z96qtky" />
                    </div>
                  </div>}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3" data-id="element-3201" data-mp="d96kj2uj67o2">
                {submitted ? <button onClick={() => {
              setModalOpen(false);
              resetForm();
            }} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-3202" data-mp="myq6ihdjihn5">
                    {isEs ? 'Cerrar' : 'Close'}
                  </button> : <>
                    <button onClick={() => setModalOpen(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-100 font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-3203" data-mp="3lpi2qtuampkf">
                      {isEs ? 'Cancelar' : 'Cancel'}
                    </button>
                    <button onClick={handleSubmit} disabled={!isValid} className="flex-1 py-2.5 bg-gradient-to-r from-haa-blue to-haa-navy hover:from-haa-midblue hover:to-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3204" data-mp="2xxhwprba2v9y">
                      {isEs ? 'Enviar' : 'Submit'}
                    </button>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}